// Copyright 2018 Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Builds a LIXI proposal package from the same options as the flat
 * Proposal Submission wizard. Attribute names follow
 * NON-CBA-STD-1SEC-1LOAN-1APPL-1SERVC.json and LmiPolicyRequestMapper.
 * Instruction used: scope-discipline.instructions.md
 */

import { DEFAULT_WIZARD_OPTIONS } from './proposal-submission-payload'
import { toPositiveInt, toPositiveNumber, todayDate } from './proposal-submission-payload'

const CONSTRUCTION_STATUSES = ['CH', 'CX', 'CO', 'CS']

// Inverse of lixi-proposal-submission-service MapConstants.loanPurposeMap.
const ABS_BY_PURPOSE = {
  PCH: 'ABS-125',
  REF: 'ABS-133',
  DCO: 'ABS-239',
  ERL: 'ABS-329',
  CFD: 'ABS-221',
  BRG: 'ABS-231',
  BCN: 'ABS-121',
  HIM: 'ABS-143',
  IRF: 'ABS-248'
}

const RESIDENTIAL_BY_SECURITY = {
  HO: 'Fully Detached House',
  UN: 'Apartment Unit Flat',
  CA: 'Apartment Unit Flat',
  SA: 'Bedsitter Bachelor',
  SM: 'Holiday Home',
  CO: 'Converted Commercial Property',
  CL: 'Converted Commercial Property',
  IN: 'Converted Industrial Property',
  VM: 'Vacant Land',
  VO: 'Vacant Land',
  GH: 'Fully Detached House',
  GU: 'Apartment Unit Flat',
  OT: 'Fully Detached House'
}

const STATUS_BY_CODE = {
  E: 'Established',
  N: 'New Building',
  V: 'Vacant Land',
  CX: 'Off the Plan'
}

// Type and builder type are alternatives in LmiPolicyRequestMapper.securityTypeStatus.
// Catalog requires both, so both are emitted. Type is listed first because the mapper
// prefers it; CX therefore needs no type or it would map to CH.
// Instruction used: debugging.instructions.md
const CONSTRUCTION_BY_CODE = {
  CH: { '@Type': 'House Land Package', '@BuilderType': 'Licensed Builder' },
  CX: { '@BuilderType': 'Licensed Builder' },
  CO: { '@Type': 'Renovation', '@BuilderType': 'Owner Builder' },
  CS: { '@BuilderType': 'Licensed Builder' }
}

export function generateLixiProposalPackage(options) {
  const opts = Object.assign({}, DEFAULT_WIZARD_OPTIONS, options || {})
  const errors = []
  const funderId = toPositiveInt(opts.funderId)
  if (funderId == null) {
    errors.push('funderId is required')
  }
  const originatorId = toPositiveInt(opts.originatorId)
  let customerId = toPositiveInt(opts.customerId)
  if (customerId == null) customerId = funderId
  if (customerId != null && customerId !== funderId && customerId !== originatorId) {
    errors.push('customerId must equal funderId or originatorId')
  }

  const requestId = lixiId(opts.requestId || ('portal-' + Date.now()))
  const state = opts.state || DEFAULT_WIZARD_OPTIONS.state
  const occupancy = opts.occupancyType || DEFAULT_WIZARD_OPTIONS.occupancyType
  const investment = occupancy === 'Investment'
  const underwriting = opts.underwritingType || DEFAULT_WIZARD_OPTIONS.underwritingType
  const securityTypeStatus = opts.securityTypeStatus || DEFAULT_WIZARD_OPTIONS.securityTypeStatus
  const isConstruction = CONSTRUCTION_STATUSES.indexOf(securityTypeStatus) !== -1
  const securityValue = toPositiveNumber(opts.securityValue, DEFAULT_WIZARD_OPTIONS.securityValue)
  const projectCosts = toPositiveInt(opts.projectCosts)
  if (isConstruction && projectCosts == null) {
    errors.push('projectCosts is required for construction security status')
  }

  const insurance = {
    '@PercentCoverRequired': 100,
    '@ProductName': opts.productName || DEFAULT_WIZARD_OPTIONS.productName,
    '@UnderwritingType': underwriting === 'DUA' ? 'eDUA' : 'Standard',
    '@PremiumInsured': 'Yes',
    '@UniqueID': 'INS-DETAILS-001',
    Premium: { '@Frequency': 'One Off' }
  }
  if (underwriting === 'DUA') {
    insurance['@ApprovedDate'] = todayDate()
  }

  const asset = {
    '@UniqueID': 'REA001',
    '@PrimaryPurpose': investment ? 'Investment' : 'Owner Occupied',
    Residential: {
      '@Type': RESIDENTIAL_BY_SECURITY[opts.securityType] || RESIDENTIAL_BY_SECURITY.HO
    },
    '@x_Address': 'ADD-SECURITY'
  }
  if ((opts.valuationMode || DEFAULT_WIZARD_OPTIONS.valuationMode) === 'full') {
    // Mapper reads PropertyFeatures only when a valuation is present.
    // Instruction used: debugging.instructions.md
    asset.PropertyFeatures = [{
      '@FloorArea': 120,
      '@LandArea': 600,
      '@LandAreaUnits': 'Square Metres',
      '@NumberOfBedrooms': 3,
      '@InternalCondition': 'Good',
      '@ExternalCondition': 'Good'
    }]
  }
  if (isConstruction) {
    asset.ConstructionDetails = Object.assign(
      { '@TotalAmount': projectCosts },
      CONSTRUCTION_BY_CODE[securityTypeStatus]
    )
  } else {
    asset['@Status'] = STATUS_BY_CODE[securityTypeStatus] || 'Established'
    if (opts.valueKind === 'estimatedValue') {
      asset.EstimatedValue = { '@Value': securityValue }
    } else {
      asset.ContractDetails = { '@ContractPriceAmount': securityValue }
    }
  }

  const application = {
    '@ProductionData': 'No',
    Address: [
      address('ADD-RESIDENTIAL', state),
      address('ADD-SECURITY', state)
    ],
    BusinessChannel: {
      '@CompanyName': 'Portal',
      Contact: {
        ContactPerson: {
          '@FirstName': 'Portal',
          '@Surname': 'User'
        },
        OfficePhone: {
          '@AustralianDialingCode': '02',
          '@Number': '90000000'
        }
      }
    },
    Funder: [{
      '@CompanyName': 'Funder',
      '@OtherIdentifier': funderId != null ? String(funderId) : undefined
    }],
    Insurance: [insurance],
    LoanDetails: [loanDetails(opts, investment)],
    Overview: {
      '@LenderApplicationReferenceNumber': requestId.slice(0, 20)
    },
    RealEstateAsset: [asset],
    Summary: {
      ServiceabilityResults: [serviceability(opts)]
    }
  }

  if (opts.borrowerType === 'Company') {
    application.CompanyApplicant = [companyApplicant(state)]
  } else {
    application.PersonApplicant = [personApplicant('Borrower', 'PA001', '1985-06-15', 'Yes', 120000)]
  }
  if (opts.includeGuarantor === true || opts.includeGuarantor === 'true') {
    application.PersonApplicant = (application.PersonApplicant || []).concat([
      personApplicant('Guarantor', 'PA002', '1960-03-20', 'No', 90000)
    ])
    application.PersonApplicant[application.PersonApplicant.length - 1]['@ApplicantType'] = 'Guarantor'
  }

  const content = { Application: application }
  const valuation = valuationBlock(opts, securityValue)
  if (valuation) {
    content.RealEstateValuation = [valuation.valuation]
    if (valuation.company) application.RelatedCompany = [valuation.company]
  }

  const payload = {
    Package: {
      '@ProductionData': 'No',
      '@UniqueID': requestId,
      Content: content,
      // getSubmissionType reads Submit.@SubmissionType. A type on the parent is ignored.
      // Instruction used: debugging.instructions.md
      Instructions: {
        LendersMortgageInsuranceInstructions: {
          Submit: {
            '@SubmissionType': 'New Application'
          }
        }
      },
      Publisher: { '@LIXICode': 'CUSTOMER_LIXI' },
      Recipient: [{ '@LIXICode': 'HELIA_LIXI' }],
      SchemaVersion: {
        '@LIXITransactionType': 'LMI',
        '@LIXIVersion': '0.1.31'
      }
    }
  }

  return { payload, errors }
}

function loanDetails(opts, investment) {
  const purpose = opts.loanPurpose || DEFAULT_WIZARD_OPTIONS.loanPurpose
  const months = toPositiveInt(opts.loanTermInMonths) || DEFAULT_WIZARD_OPTIONS.loanTermInMonths
  return {
    '@AmountRequested': toPositiveNumber(opts.loanAmount, DEFAULT_WIZARD_OPTIONS.loanAmount),
    '@LoanType': investment ? 'Mortgage Loan' : 'Amortising Home Loan',
    '@UniqueID': 'LOAN-ID-1',
    LendingPurpose: [{ '@ABSLendingPurposeCode': ABS_BY_PURPOSE[purpose] || ABS_BY_PURPOSE.PCH }],
    LoanPurpose: {
      '@PrimaryPurpose': investment ? 'Investment Residential' : 'Owner Occupied'
    },
    // P+I / Variable maps to PIV. A payment duration is copied into the
    // interest-only period and fails I_MISMATCH_REPAYMENT_TYPE, so omit it.
    // Instruction used: debugging.instructions.md
    Term: {
      '@TotalTermDuration': months,
      '@TotalTermUnits': 'Months',
      '@PaymentType': 'Principal and Interest',
      '@InterestType': 'Variable'
    },
    Security: [{ '@x_Security': 'REA001' }]
  }
}

function serviceability(opts) {
  const type = opts.serviceabilityType || DEFAULT_WIZARD_OPTIONS.serviceabilityType
  const result = { '@UniqueID': 'SMRY-SR-001' }
  if (type === 'DSR') {
    result['@DSR'] = 30
  } else if (type !== 'NDS') {
    result['@NSR'] = 1.5
  }
  // A person applicant is rejected unless one result has disposable income.
  // Instruction used: debugging.instructions.md
  result.NetDisposableIncome = { '@Amount': 500, '@Frequency': 'Monthly' }
  return result
}

function valuationBlock(opts, securityValue) {
  const mode = opts.valuationMode || DEFAULT_WIZARD_OPTIONS.valuationMode
  if (mode === 'inPrinciple') return null
  if (mode === 'valex') {
    const valuationId = String(opts.valuationId || '').trim()
    const valuation = {
      '@UniqueID': 'VALUATION-001',
      '@Marketability': 'Average',
      '@ForecastStandardDeviation': 10,
      '@TotalValuedAmount': securityValue,
      '@x_Asset': 'REA001'
    }
    if (valuationId) valuation['@ValuerAssignedID'] = valuationId
    return { valuation: valuation }
  }
  return {
    valuation: {
      '@UniqueID': 'VALUATION-001',
      '@Marketability': 'Average',
      '@ForecastStandardDeviation': 10,
      '@TotalValuedAmount': securityValue,
      '@ValuationType': 'Full Registered',
      '@ValuedDate': opts.valuationDate || todayDate(),
      '@x_Valuer': 'VALUERCOMPANY-001',
      '@x_Asset': 'REA001',
      RiskAnalysis: {
        RiskRating: [
          'Environmental',
          'Improvements',
          'Land',
          'LocalEconomy',
          'LocationNeighbourhood',
          'MarketSegment',
          'MarketVolatility',
          'RecentMarketDirection'
        ].map(function (ratingType, index) {
          return {
            '@Rating': '1-Low',
            '@RatingType': ratingType,
            '@SequenceNumber': index + 1
          }
        })
      },
      ValuationFirm: {
        '@CompanyName': (opts.valuerCompanyName || DEFAULT_WIZARD_OPTIONS.valuerCompanyName).trim(),
        PersonName: {
          '@FirstName': 'Sample',
          '@Surname': 'Valuer'
        }
      }
    },
    company: {
      '@UniqueID': 'VALUERCOMPANY-001',
      '@CompanyName': (opts.valuerCompanyName || DEFAULT_WIZARD_OPTIONS.valuerCompanyName).trim()
    }
  }
}

function personApplicant(surname, id, dateOfBirth, primary, income) {
  return {
    '@ApplicantType': 'Borrower',
    '@DateOfBirth': dateOfBirth,
    '@PrimaryApplicant': primary,
    '@UniqueID': id,
    '@ResidencyStatus': 'Permanently in Australia',
    '@MaritalStatus': 'Single',
    Employment: [{
      PAYG: {
        '@ANZSCOOccupationCode': '200000',
        '@Basis': 'Full Time',
        '@Status': 'Primary',
        Income: {
          '@GrossSalaryAmount': income,
          '@GrossSalaryFrequency': 'Yearly'
        }
      }
    }],
    Contact: {
      CurrentAddress: { '@x_ResidentialAddress': 'ADD-RESIDENTIAL' }
    },
    PersonName: {
      '@FirstName': 'Sample',
      '@Surname': surname
    },
    ProofOfIdentity: [{
      '@DocumentType': 'Australian Drivers Licence',
      // Mapped to driversLicenseNumber, then bureau photo-ID number (maxLength 9).
      // Instruction used: debugging.instructions.md
      '@DocumentNumber': '123456789'
    }]
  }
}

function companyApplicant(state) {
  return {
    '@UniqueID': 'CA001',
    '@ApplicantType': 'Borrower',
    '@PrimaryApplicant': 'Yes',
    '@BusinessName': 'Sample Pty Ltd',
    '@ABN': '51000000001',
    '@TypeOfIncorporation': 'Pty Ltd',
    Contact: {
      '@x_RegisteredAddress': 'ADD-RESIDENTIAL'
    },
    Income: [{
      '@CompanyProfitAfterTax': 250000
    }]
  }
}

function address(id, state) {
  return {
    '@AustralianPostCode': '2000',
    '@AustralianState': state,
    '@Country': 'AU',
    '@Suburb': 'Sydney',
    '@Type': 'Standard',
    '@UniqueID': id,
    Standard: {
      '@StreetName': 'Sample',
      '@StreetNumber': '1',
      '@StreetType': 'Street'
    }
  }
}

function lixiId(value) {
  const cleaned = String(value).replace(/[^a-zA-Z0-9-_.]/g, '-')
  return /^[a-zA-Z_]/.test(cleaned) ? cleaned : ('P-' + cleaned)
}
